import { useMemo, useState } from "react"
import {
  useGetDestinationAuditQuery,
  type TDestinationAuditAd,
  type TDestinationTrackingVariant,
} from "~/ui-rtk/api/destinationAuditApi"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"

export const RANGE_OPTIONS = [
  { label: "Last 7 days", value: "7d" },
  { label: "Last 14 days", value: "14d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
]

export const CHANNEL_OPTIONS = [
  { label: "All channels", value: "" },
  { label: "Meta", value: "meta" },
  { label: "Google Ads", value: "google_ads" },
  { label: "TikTok Ads", value: "tiktok_ads" },
  { label: "YouTube Ads", value: "youtube_ads" },
]

/**
 * The ads carrying a given finding code, or all of them when nothing is
 * selected. Drives the issue-card -> table filter.
 */
export function selectAdsWithFinding(
  ads: TDestinationAuditAd[],
  code: string | null,
): TDestinationAuditAd[] {
  if (!code) return ads
  return ads.filter(ad => ad.findings.some(f => f.code === code))
}

/**
 * Channels running more than one distinct (source, medium) pair — their traffic
 * is being split across labels in every downstream report. This is the only
 * thing that puts the "split" badge on a row, and it fails silently if it
 * drifts: the badge simply stops appearing and the account looks healthy.
 */
export function findFragmentedChannels(
  variants: TDestinationTrackingVariant[],
): Set<string> {
  const counts = new Map<string, number>()
  for (const variant of variants) {
    counts.set(variant.channel, (counts.get(variant.channel) ?? 0) + 1)
  }
  return new Set(
    [...counts.entries()].filter(([, n]) => n > 1).map(([channel]) => channel),
  )
}

export default function useConnect() {
  const company = useCurrentCompany()
  const [range, setRange] = useState("30d")
  const [channel, setChannel] = useState("")
  /**
   * The live check fetches every distinct destination, so it is a deliberate
   * button press rather than something that fires on page load. Once armed it
   * stays on for subsequent range/channel changes.
   */
  const [live, setLive] = useState(false)
  const [activeCode, setActiveCode] = useState<string | null>(null)

  // `/cli/*` resolves the company by slug OR id, and checks the session has
  // access either way. The browser only carries the id, so send that.
  const companyId = company?.id ?? ""

  const { data, isFetching, isError, refetch } = useGetDestinationAuditQuery(
    { company: companyId, range, channel: channel || undefined, live },
    { skip: !companyId },
  )

  // Clicking an issue card filters the table below it, so the summary and the
  // detail are one interaction instead of two disconnected lists.
  const visibleAds: TDestinationAuditAd[] = useMemo(
    () => selectAdsWithFinding(data?.ads ?? [], activeCode),
    [data, activeCode],
  )

  const fragmentedChannels = useMemo(
    () => findFragmentedChannels(data?.trackingVariants ?? []),
    [data],
  )

  return {
    data,
    isFetching,
    isError,
    refetch,
    range,
    setRange,
    channel,
    setChannel,
    live,
    setLive,
    activeCode,
    setActiveCode,
    visibleAds,
    fragmentedChannels,
    hasCompany: Boolean(companyId),
  }
}
